import { getPreComputeModelScreenerData } from "@pages/managed-accounts/model-screener/utils";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { APIExtRoutes } from "@constants";
import {
  createModelScreenerFilterSetting,
  deleteModelScreenerFilterSetting,
  exportModelScreenerExcel,
  getModelScreenerData,
  getModelScreenerDataByDateRange,
  getModelScreenerFilterSettingById,
  getModelScreenerFilterSettings,
  putModelScreenerFavoriteModels,
  updateModelScreenerFilterSetting,
  setFilterSettingAsDefault,
  getModelScreenerColumnSettings,
  createMSColumnSetting,
  updateMSColumnSetting,
  getMSColumnSettingById,
  deleteModelScreenerFilterColumn,
  setMSColumnSettingAsDefault,
} from "@services/model-screener/modelScreenerService";
import { defaultTo, isEmpty, isEqual } from "lodash";
import {
  ModelScreenerData,
  ModelScreenerExportModelsDto,
  ModelScreenerMainHeader,
  ModelScreenerSubHeader,
} from "@models/model-screener/entities/modelScreenerData";
import {
  ModelScreenerBasicFilters,
  ModelScreenerColumnSettingDTO,
  ModelScreenerFilterDTO,
} from "@models/model-screener/entities/modelScreenerFilter";
import {
  MSFavouriteFilterValues,
  MSSelectedFilterValues,
} from "@models/model-screener/enums/filterValues";
import { MSDefaultDelayTimeMs } from "@pages/managed-accounts/model-screener/constant";
import { APIResponse } from "@models/core";
import {
  getModelScreenerDefaultMainFilter,
  getPreComputeModelScreenerFilterData,
  handleApplyFilter,
} from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/utils";
import { MSFilterDefaultValueConstants } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/constants";
import { RootState } from "@redux/store";
import { generateDefaultColumnSetting } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/column-setting/utils";

type MSFilterParams = {
  basicFilter?: ModelScreenerBasicFilters;
  mainFilter?: ModelScreenerFilterDTO;
};

type MSColumnSettingParams = {
  columnSetting: ModelScreenerColumnSettingDTO;
};

interface ModelScreenerStore {
  originalData: ModelScreenerData;
  filteredData: ModelScreenerData;
  selectedRowIds: Record<string, boolean>;
  favouritedRowIds: Record<string, boolean>;
  currentSelectedDateRange: [Date | null, Date | null];
  ui: {
    columnVisibility: Record<string, boolean>;
    columnFilterValues: Record<string, string[]>;
    isLoading: boolean;
    isLoadingDataByDateRange: boolean;
    isPatchingFavoritedModels?: boolean;
    patchingFavoritedModelIds: Record<string, boolean>;
    isFiltering: boolean;
    isReloading: boolean;
    isFilterApplied?: boolean;
    isExportingExcel?: boolean;
    numberOfFiltersApplied: number;
    numberOfColumnsApplied: number;
    isLoadingModelScreenerFilterSetting?: boolean;
    isDeletingModelScreenerFilterSetting?: boolean;
    isSavingModelScreenerFilterSetting?: boolean;
    isSavingModelScreenerColumnSetting?: boolean;
    isLoadingModelScreenerColumnSetting?: boolean;
  };
  tableScrollProps: {
    scrolledTop: boolean;
    scrolledLeft: boolean;
    shouldHideToolbar: boolean;
  };
  basicFilters: ModelScreenerBasicFilters;
  filterSettings: ModelScreenerFilterDTO[];
  columnSettings: ModelScreenerColumnSettingDTO[];
  internal: {
    viaSearchSubHeaderIds: Record<string, boolean>;
    subHeaderFlattenedIndexes: Record<string, number>;
    combinedSearchValues: Record<string, string[]>;
    displayingRowIds: Record<string, boolean>;
    filterItemFieldPaths: Record<string, any>;
    originalEditingFilter: ModelScreenerFilterDTO | null;
    currentEditingFilter: ModelScreenerFilterDTO | null;
    currentEditingFilterDefaultData: ModelScreenerFilterDTO | null;
    appliedFilter: ModelScreenerFilterDTO | null;

    // Column setting
    defaultColumnSettingData: ModelScreenerColumnSettingDTO | null;
    currentEditingColumnSetting: ModelScreenerColumnSettingDTO | null;
  };
}

const initialState: ModelScreenerStore = {
  originalData: {
    headers: [],
    data: [],
  },
  filteredData: {
    headers: [],
    data: [],
  },
  selectedRowIds: {},
  favouritedRowIds: {},
  currentSelectedDateRange: [null, null],
  ui: {
    isLoading: false,
    isLoadingDataByDateRange: false,
    isPatchingFavoritedModels: false,
    isFiltering: false,
    isReloading: false,
    isFilterApplied: false,
    patchingFavoritedModelIds: {},
    columnVisibility: {},
    columnFilterValues: {},
    isExportingExcel: false,
    numberOfFiltersApplied: 0,
    numberOfColumnsApplied: 0,
    isLoadingModelScreenerFilterSetting: false,
    isDeletingModelScreenerFilterSetting: false,
    isSavingModelScreenerFilterSetting: false,
    isSavingModelScreenerColumnSetting: false,
  },
  tableScrollProps: {
    scrolledTop: true,
    scrolledLeft: true,
    shouldHideToolbar: false,
  },
  basicFilters: {
    searchValue: null,
    favourited: MSFavouriteFilterValues.All,
    selected: MSSelectedFilterValues.All,
  },
  filterSettings: [],
  columnSettings: [],
  internal: {
    viaSearchSubHeaderIds: {},
    subHeaderFlattenedIndexes: {},
    combinedSearchValues: {},
    displayingRowIds: {},
    filterItemFieldPaths: {},
    originalEditingFilter: null,
    currentEditingFilter: null,
    currentEditingFilterDefaultData: null,
    appliedFilter: null,
    defaultColumnSettingData: null,
    currentEditingColumnSetting: null,
  },
};

const modelScreenerSlice = createSlice({
  name: "modelScreener",
  initialState,
  reducers: {
    // Data
    resetTemporaryData: (state) => {
      state.selectedRowIds = initialState.selectedRowIds;
      state.basicFilters = initialState.basicFilters;
      state.tableScrollProps = initialState.tableScrollProps;

      state.filterSettings = initialState.filterSettings;
      state.columnSettings = initialState.columnSettings;

      state.internal = initialState.internal;
    },

    // Select row
    addSelectedRowIds: (state, action: PayloadAction<string>) => {
      const selectedRowIds = state.selectedRowIds;
      if (selectedRowIds[action.payload]) return;
      selectedRowIds[action.payload] = true;
    },
    removeSelectedRowIds: (state, action: PayloadAction<string>) => {
      const selectedRowIds = state.selectedRowIds;
      if (!selectedRowIds[action.payload]) return;
      delete selectedRowIds[action.payload];
    },
    selectAllDisplayingRowIds: (state) => {
      const displayingRowIds = state.internal.displayingRowIds;
      const currentSelectedRowIds = state.selectedRowIds;
      Object.keys(displayingRowIds).forEach((id) => {
        if (!currentSelectedRowIds[id]) {
          currentSelectedRowIds[id] = true;
        }
      });
      state.selectedRowIds = currentSelectedRowIds;
    },
    deselectAllDisplayingRowIds: (state) => {
      const displayingRowIds = state.internal.displayingRowIds;
      const currentSelectedRowIds = state.selectedRowIds;
      Object.keys(displayingRowIds).forEach((id) => {
        if (currentSelectedRowIds[id]) {
          delete currentSelectedRowIds[id];
        }
      });
      state.selectedRowIds = currentSelectedRowIds;
    },
    setDisplayingRowIds: (state, action: PayloadAction<string[]>) => {
      const rowIds: Record<string, boolean> = {};
      action.payload.forEach((id) => {
        rowIds[id] = true;
      });

      state.internal.displayingRowIds = rowIds;
    },

    // Favorited model
    setFavoritedRowIds: (state, action: PayloadAction<string[]>) => {
      const newFavoritedRowIds: Record<string, boolean> = {};
      action.payload.forEach((id) => {
        newFavoritedRowIds[id] = true;
      });
      state.favouritedRowIds = newFavoritedRowIds;
    },

    // Table scroll props
    setTableScrolledTop: (state, action: PayloadAction<boolean>) => {
      state.tableScrollProps.scrolledTop = action.payload;
    },
    setTableScrolledLeft: (state, action: PayloadAction<boolean>) => {
      state.tableScrollProps.scrolledLeft = action.payload;
    },
    setShouldHideToolbar: (state, action: PayloadAction<boolean>) => {
      state.tableScrollProps.shouldHideToolbar = action.payload;
    },

    // Filters
    setBasicFilters: (
      state,
      action: PayloadAction<ModelScreenerBasicFilters>,
    ) => {
      applyFilterToState({
        state,
        basicFilter: action.payload,
      });
    },
    toggleFiltering: (state, action: PayloadAction<boolean>) => {
      state.ui.isFiltering = action.payload;
    },
    toggleReloading: (state, action: PayloadAction<boolean>) => {
      state.ui.isReloading = action.payload;
    },
    checkFilterApplied: (state) => {
      const selectedFilterApplied = !isEqual(
        state.basicFilters.selected,
        initialState.basicFilters.selected,
      );
      const favouritedFilterApplied = !isEqual(
        state.basicFilters.favourited,
        initialState.basicFilters.favourited,
      );

      state.ui.isFilterApplied =
        selectedFilterApplied || favouritedFilterApplied;
    },
    setFilters: (state, action: PayloadAction<MSFilterParams>) => {
      const { basicFilter, mainFilter } = action.payload;
      applyFilterToState({ state, basicFilter, mainFilter });
    },
    resetMainFilterData: (state) => {
      state.internal.currentEditingFilter =
        state.internal.originalEditingFilter;
    },
    setFilterSettings: (
      state,
      action: PayloadAction<{
        filterSettings: ModelScreenerFilterDTO[];
        currentEditingFilter: ModelScreenerFilterDTO;
      }>,
    ) => {
      state.filterSettings = action.payload.filterSettings;
      state.internal.currentEditingFilter = action.payload.currentEditingFilter;
    },
    setDefaultFilterSetting: (state) => {
      const originalFilter = state.internal.currentEditingFilterDefaultData;
      if (!originalFilter) return;
      state.internal.currentEditingFilter = originalFilter;
    },
    resetAppliedFilter: (state) => {
      state.internal.appliedFilter = null;
    },
    setColumnSetting: (state, action: PayloadAction<MSColumnSettingParams>) => {
      const { columnSetting } = action.payload;
      applyColumnSettingToState({ state, columnSetting });
    },
    setColumnSettings: (
      state,
      action: PayloadAction<{
        columnSettings: ModelScreenerColumnSettingDTO[];
      }>,
    ) => {
      state.columnSettings = action.payload.columnSettings;
    },
  },
  extraReducers(builder) {
    // Get model screener data
    builder.addCase(getModelScreenerDataThunk.pending, (state, action) => {
      const { isReloadDataByDateRange } = action?.meta?.arg || {};

      if (!!isReloadDataByDateRange) {
        state.ui.isLoadingDataByDateRange = true;
        return;
      }

      state.ui.isLoading = true;
    });
    builder.addCase(getModelScreenerDataThunk.fulfilled, (state, action) => {
      handleModelScreenerDataResponse({
        state,
        action,
        metaData: action.meta?.arg,
      });
    });
    builder.addCase(getModelScreenerDataThunk.rejected, (state, _) => {
      state.ui.isLoading = false;
      state.ui.isLoadingDataByDateRange = false;
    });

    // Load model screener data by date range
    builder.addCase(
      getModelScreenerDataByDateRangeThunk.pending,
      (state, _) => {
        state.ui.isFiltering = true;
        state.ui.isLoadingDataByDateRange = true;
      },
    );
    builder.addCase(
      getModelScreenerDataByDateRangeThunk.fulfilled,
      (state, action) => {
        handleModelScreenerDataResponse({
          state,
          action,
          metaData: action.meta?.arg,
        });
      },
    );
    builder.addCase(
      getModelScreenerDataByDateRangeThunk.rejected,
      (state, _) => {
        state.ui.isLoadingDataByDateRange = false;
      },
    );

    // Patch model screener favorite model
    builder.addCase(putModelScreenerFavoriteModelThunk.pending, (state, _) => {
      state.ui.isPatchingFavoritedModels = true;
    });
    builder.addCase(
      putModelScreenerFavoriteModelThunk.fulfilled,
      (state, _) => {
        state.ui.isPatchingFavoritedModels = false;
      },
    );
    builder.addCase(putModelScreenerFavoriteModelThunk.rejected, (state, _) => {
      state.ui.isPatchingFavoritedModels = false;
    });

    // Export excel
    builder.addCase(exportModelScreenerExcelThunk.pending, (state, _) => {
      state.ui.isExportingExcel = true;
    });
    builder.addCase(exportModelScreenerExcelThunk.fulfilled, (state, _) => {
      state.ui.isExportingExcel = false;
    });
    builder.addCase(exportModelScreenerExcelThunk.rejected, (state, _) => {
      state.ui.isExportingExcel = false;
    });

    // Filter setting
    builder.addCase(
      loadModelScreenerFilterSettingsThunk.fulfilled,
      (state, action) => {
        let actionData = action.payload.data;
        if (!actionData) return;

        const defaultUserFilter = actionData.find((data) => data.isUserDefault);
        if (!defaultUserFilter) {
          actionData = actionData.map((data) => ({
            ...data,
            isUserDefault: data.isSystemDefault,
          }));
        }

        state.filterSettings = actionData;
      },
    );
    builder.addCase(
      loadModelScreenerFilterSettingByIdThunk.pending,
      (state, _) => {
        state.ui.isLoadingModelScreenerFilterSetting = true;
      },
    );
    builder.addCase(
      loadModelScreenerFilterSettingByIdThunk.fulfilled,
      (state, action) => {
        state.ui.isLoadingModelScreenerFilterSetting = false;
        const filterSetting = action.payload?.data;
        if (!filterSetting) return;
        state.internal.currentEditingFilter = filterSetting;
      },
    );
    builder.addCase(
      loadModelScreenerFilterSettingByIdThunk.rejected,
      (state, _) => {
        state.ui.isLoadingModelScreenerFilterSetting = false;
      },
    );

    builder.addCase(
      deleteModelScreenerFilterSettingThunk.fulfilled,
      (state, action) => {
        if (!action.payload.isSuccess) return;
        state.filterSettings = state.filterSettings.filter(
          (filterSetting) => filterSetting.id !== action.meta.arg,
        );
      },
    );
    builder.addCase(
      updateModelScreenerFilterSettingThunk.pending,
      (state, _) => {
        state.ui.isSavingModelScreenerFilterSetting = true;
      },
    );
    builder.addCase(
      updateModelScreenerFilterSettingThunk.fulfilled,
      (state, _) => {
        state.ui.isSavingModelScreenerFilterSetting = false;
      },
    );
    builder.addCase(
      updateModelScreenerFilterSettingThunk.rejected,
      (state, _) => {
        state.ui.isSavingModelScreenerFilterSetting = false;
      },
    );

    builder.addCase(
      setModelScreenerFilterSettingAsDefaultThunk.fulfilled,
      (state, action) => {
        if (!action.payload.isSuccess) return;

        const targetId = action.meta.arg;

        state.filterSettings = state.filterSettings
          .map((setting) => ({
            ...setting,
            isUserDefault: setting.id === targetId,
          }))
          .sort((a, b) => {
            if (a.isSystemDefault && !b.isSystemDefault) return -1;
            if (!a.isSystemDefault && b.isSystemDefault) return 1;
            if (a.isUserDefault && !b.isUserDefault) return -1;
            if (!a.isUserDefault && b.isUserDefault) return 1;
            return a.name.localeCompare(b.name);
          });
      },
    );
    // Column settings
    builder.addCase(
      loadModelScreenerColumnSettingsThunk.fulfilled,
      (state, action) => {
        let actionData = action.payload.data;
        if (!actionData) return;

        state.columnSettings = actionData;
      },
    );
    builder.addCase(updateMSColumnSettingThunk.pending, (state, _) => {
      state.ui.isSavingModelScreenerColumnSetting = true;
    });
    builder.addCase(updateMSColumnSettingThunk.fulfilled, (state, _) => {
      state.ui.isSavingModelScreenerColumnSetting = false;
    });
    builder.addCase(updateMSColumnSettingThunk.rejected, (state, _) => {
      state.ui.isSavingModelScreenerColumnSetting = false;
    });

    builder.addCase(loadMSColumnSettingByIdThunk.pending, (state, _) => {
      state.ui.isLoadingModelScreenerColumnSetting = true;
    });
    builder.addCase(loadMSColumnSettingByIdThunk.fulfilled, (state, action) => {
      state.ui.isLoadingModelScreenerColumnSetting = false;
      const columnSetting = action.payload?.data;
      if (!columnSetting) return;
      state.internal.currentEditingColumnSetting = columnSetting;
    });
    builder.addCase(loadMSColumnSettingByIdThunk.rejected, (state, _) => {
      state.ui.isLoadingModelScreenerColumnSetting = false;
    });
    builder.addCase(
      deleteModelScreenerColumnSettingThunk.fulfilled,
      (state, action) => {
        if (!action.payload.isSuccess) return;
        state.columnSettings = state.columnSettings.filter(
          (columnSetting) => columnSetting.id !== action.meta.arg,
        );
      },
    );
    builder.addCase(
      setMSColumnSettingAsDefaultThunk.fulfilled,
      (state, action) => {
        if (!action.payload.isSuccess) return;

        const targetId = action.meta.arg;

        state.columnSettings = state.columnSettings
          .map((setting) => ({
            ...setting,
            isUserDefault: setting.id === targetId,
          }))
          .sort((a, b) => {
            if (a.isSystemDefault && !b.isSystemDefault) return -1;
            if (!a.isSystemDefault && b.isSystemDefault) return 1;
            if (a.isUserDefault && !b.isUserDefault) return -1;
            if (!a.isUserDefault && b.isUserDefault) return 1;
            return a.name.localeCompare(b.name);
          });
      },
    );
  },
});

const handleModelScreenerDataResponse = ({
  state,
  action,
  metaData = { fromDate: null, toDate: null },
}: {
  state: ModelScreenerStore;
  action: PayloadAction<APIResponse<ModelScreenerData>>;
  metaData?: {
    fromDate?: Date | null;
    toDate?: Date | null;
    isReloadDataByDateRange?: boolean;
  };
}) => {
  const actionData = action.payload.data;
  if (!actionData) return;
  state.ui.isFiltering = true;
  state.originalData = actionData;
  state.currentSelectedDateRange = [
    defaultTo(metaData.fromDate, null),
    defaultTo(metaData.toDate, null),
  ];

  const {
    favouritedRowIds,
    combinedSearchValues,
    viaSearchSubHeaderIds,
    subHeaderFlattenedIndexes,
    columnVisibility,
  } = getPreComputeModelScreenerData(actionData);

  const filterData = actionData.filter;
  if (!!filterData) {
    if (isEmpty(state.internal.filterItemFieldPaths)) {
      const { compiledFieldPathRecord } = getPreComputeModelScreenerFilterData({
        data: filterData,
      });
      state.internal.filterItemFieldPaths = compiledFieldPathRecord;
    }

    state.internal.currentEditingFilter =
      state.internal.originalEditingFilter =
      state.internal.appliedFilter =
        filterData;
    state.internal.currentEditingFilterDefaultData =
      getModelScreenerDefaultMainFilter(filterData);
  }

  state.internal.viaSearchSubHeaderIds = viaSearchSubHeaderIds;
  state.internal.subHeaderFlattenedIndexes = subHeaderFlattenedIndexes;
  state.internal.combinedSearchValues = combinedSearchValues;
  state.ui.columnVisibility = columnVisibility;
  state.favouritedRowIds = favouritedRowIds;

  if (!!actionData.columnSetting) {
    state.internal.currentEditingColumnSetting = actionData.columnSetting;
  }
  state.internal.defaultColumnSettingData = generateDefaultColumnSetting(
    actionData.headers,
  );

  applyFilterToState({ state });
  applyColumnSettingToState({ state });

  state.ui.isLoading = false;
  state.ui.isLoadingDataByDateRange = false;
};

const applyFilterToState = ({
  state,
  basicFilter,
  mainFilter,
}: {
  state: ModelScreenerStore;
  basicFilter?: ModelScreenerBasicFilters;
  mainFilter?: ModelScreenerFilterDTO;
}) => {
  const targetBasicFilter = basicFilter ?? state.basicFilters;
  const targetMainFilter = mainFilter ?? state.internal.appliedFilter;

  if (!targetMainFilter) return;

  const {
    combinedSearchValues,
    viaSearchSubHeaderIds,
    subHeaderFlattenedIndexes,
  } = state.internal;

  const { data, numOfFiltersApplied } = handleApplyFilter({
    originalData: state.originalData,
    basicFilter: targetBasicFilter,
    formData: targetMainFilter,
    internalData: {
      combinedSearchValues: combinedSearchValues,
      viaSearchSubHeaderIds: viaSearchSubHeaderIds,
      subHeaderFlattenedIndexes: subHeaderFlattenedIndexes,
      favouritedRowIds: state.favouritedRowIds
    },
  });

  const currentHeaders = state.filteredData.headers;
  state.filteredData = {
    ...data,
    headers:
      isEmpty(currentHeaders) || !state.internal.currentEditingColumnSetting
        ? data.headers
        : currentHeaders,
  };

  state.ui.numberOfFiltersApplied = numOfFiltersApplied;
  state.internal.currentEditingFilter = state.internal.appliedFilter =
    targetMainFilter;
  state.basicFilters = targetBasicFilter;
};

const applyColumnSettingToState = ({
  state,
  columnSetting,
}: {
  state: ModelScreenerStore;
  columnSetting?: ModelScreenerColumnSettingDTO;
}) => {
  const targetColumnSetting =
    columnSetting ?? state.internal.currentEditingColumnSetting;

  if (!targetColumnSetting) return;

  const columns = targetColumnSetting.data.flatMap(
    (item) => item.columnSettings || [],
  );

  let numOfHiddenColumns = 0;
  columns.forEach(({ subHeaderId, isVisible }) => {
    if (subHeaderId && subHeaderId in state.ui.columnVisibility) {
      state.ui.columnVisibility[subHeaderId] = isVisible;
      if (!isVisible) numOfHiddenColumns++;
    }
  });
  state.ui.numberOfColumnsApplied = numOfHiddenColumns;

  const headerMap = new Map<string, ModelScreenerMainHeader>();
  for (const header of state.filteredData.headers) {
    headerMap.set(header.id, {
      ...header,
      subHeaders: [...header.subHeaders],
    });
  }

  const orderedHeaders: ModelScreenerMainHeader[] = [];
  const updatedIds = new Set<string>();

  for (const main of targetColumnSetting.data) {
    if (!main.headerId) continue;
    const header = headerMap.get(main.headerId);
    if (!header) continue;

    const subHeaderMap = new Map(
      header.subHeaders.map((sh) => [sh.id, { ...sh }]),
    );
    const newSubHeaders: ModelScreenerSubHeader[] = [];

    for (const sub of main.columnSettings || []) {
      if (!sub.subHeaderId) continue;
      const subHeader = subHeaderMap.get(sub.subHeaderId);
      if (subHeader) {
        subHeader.order = sub.order;
        newSubHeaders.push(subHeader);
      }
    }

    header.order = main.order;
    header.subHeaders = newSubHeaders;
    orderedHeaders.push(header);
    updatedIds.add(header.id);
  }

  const unchangedHeaders = state.filteredData.headers.filter(
    (h) => !updatedIds.has(h.id),
  );
  state.filteredData.headers = [...unchangedHeaders, ...orderedHeaders];
  columnSetting && (state.internal.currentEditingColumnSetting = columnSetting);
};

export const {
  resetTemporaryData,
  addSelectedRowIds,
  removeSelectedRowIds,
  setFavoritedRowIds,
  selectAllDisplayingRowIds,
  deselectAllDisplayingRowIds,
  setTableScrolledTop,
  setTableScrolledLeft,
  setShouldHideToolbar,
  toggleFiltering,
  checkFilterApplied,
  setDisplayingRowIds,
  setFilters,
  resetMainFilterData,
  setFilterSettings,
  setDefaultFilterSetting,
  resetAppliedFilter,
  setColumnSetting,
  setColumnSettings,
} = modelScreenerSlice.actions;

export const getModelScreenerDataThunk = createAsyncThunk(
  APIExtRoutes.modelScreeners,
  async (
    {
      signal,
    }: {
      signal?: AbortSignal;
      isReloadDataByDateRange?: boolean;
    },
    { getState },
  ) => {
    const rootState = getState() as RootState;
    return await getModelScreenerData({
      signal,
      filter: rootState.modelScreener.internal.appliedFilter,
      columnSetting:
        rootState.modelScreener.internal.currentEditingColumnSetting,
    });
  },
);

export const getModelScreenerDataByDateRangeThunk = createAsyncThunk(
  APIExtRoutes.modelScreenersDateRange,
  async (
    {
      signal,
      fromDate,
      toDate,
    }: {
      signal?: AbortSignal;
      fromDate: Date | null;
      toDate: Date | null;
    },
    { getState },
  ) => {
    const state = getState() as RootState;
    return await getModelScreenerDataByDateRange({
      signal,
      fromDate,
      toDate,
      filter: state.modelScreener.internal.appliedFilter,
      columnSetting: state.modelScreener.internal.currentEditingColumnSetting,
    });
  },
);

export const putModelScreenerFavoriteModelThunk = createAsyncThunk(
  APIExtRoutes.modelScreenersFavorited,
  async (modelIds: string[]) => {
    return await putModelScreenerFavoriteModels(modelIds);
  },
);

export const exportModelScreenerExcelThunk = createAsyncThunk(
  APIExtRoutes.modelScreenerExport,
  async (exportDto: ModelScreenerExportModelsDto, { getState }) => {
    const state = getState() as RootState;
    return await exportModelScreenerExcel({
      ...exportDto,
      columnSetting: state.modelScreener.internal.currentEditingColumnSetting,
    });
  },
);

export const updateModelScreenerFilters = createAsyncThunk(
  "modelScreeners/updateModelScreenerFilters",
  async (
    {
      basicFilter,
      mainFilter,
    }: {
      basicFilter?: ModelScreenerBasicFilters;
      mainFilter?: ModelScreenerFilterDTO;
    },
    { dispatch },
  ) => {
    dispatch(toggleFiltering(true));
    dispatch(setFilters({ basicFilter, mainFilter }));
    setTimeout(
      () => dispatch(toggleFiltering(false)),
      MSFilterDefaultValueConstants.StandardDelayTimeMs,
    );
  },
);

export const setModelScreenerDisplayingRowIds = createAsyncThunk(
  "modelScreeners/setDisplayingRowIds",
  async (rowIds: string[], { dispatch }) => {
    dispatch(toggleFiltering(true));
    dispatch(setDisplayingRowIds(rowIds));

    setTimeout(() => {
      dispatch(toggleFiltering(false));
    }, MSDefaultDelayTimeMs);
  },
);

export const loadModelScreenerFilterSettingsThunk = createAsyncThunk(
  "modelScreeners/loadModelScreenerFilterSettings",
  async () => {
    return await getModelScreenerFilterSettings();
  },
);

export const loadModelScreenerFilterSettingByIdThunk = createAsyncThunk(
  "modelScreeners/loadModelScreenerFilterSettingById",
  async (
    {
      selectedFilterSettingId,
      signal,
    }: {
      selectedFilterSettingId: string;
      signal?: AbortSignal;
    },
    { getState },
  ) => {
    const rootState = getState() as RootState;
    const modelScreenerState = rootState.modelScreener;
    const currentEditingFilter =
      modelScreenerState.internal.currentEditingFilter;
    const currentSettings = modelScreenerState.filterSettings;

    if (!currentEditingFilter || !currentSettings || isEmpty(currentSettings))
      return;

    const selectedFilterSetting = currentSettings.find(
      (setting) => setting.id === selectedFilterSettingId,
    );

    if (!selectedFilterSetting) return;

    return await getModelScreenerFilterSettingById({
      signal: signal,
      filterSettingId: selectedFilterSettingId,
      baseFilter: currentEditingFilter,
    });
  },
);

export const deleteModelScreenerFilterSettingThunk = createAsyncThunk(
  "modelScreeners/deleteModelScreenerFilterSetting",
  async (filterSettingId: string) => {
    return await deleteModelScreenerFilterSetting(filterSettingId);
  },
);

export const createModelScreenerFilterSettingThunk = createAsyncThunk(
  "modelScreeners/createModelScreenerFilterSetting",
  async ({
    signal,
    filterSetting,
  }: {
    signal?: AbortSignal;
    filterSetting: ModelScreenerFilterDTO;
  }) => {
    return await createModelScreenerFilterSetting(filterSetting, signal);
  },
);

export const updateModelScreenerFilterSettingThunk = createAsyncThunk(
  "modelScreeners/updateModelScreenerFilterSetting",
  async ({
    signal,
    filterSetting,
  }: {
    signal?: AbortSignal;
    filterSetting: ModelScreenerFilterDTO;
  }) => {
    return await updateModelScreenerFilterSetting(filterSetting, signal);
  },
);

export const setModelScreenerFilterSettingAsDefaultThunk = createAsyncThunk(
  "modelScreeners/setModelScreenerFilterSettingAsDefault",
  async (filterSettingId: string) => {
    return await setFilterSettingAsDefault(filterSettingId);
  },
);

export const updateModelScreenerColumnSetting = createAsyncThunk(
  "modelScreeners/updateModelScreenerColumnSetting",
  async (
    {
      columnSetting,
    }: {
      columnSetting: ModelScreenerColumnSettingDTO;
    },
    { dispatch },
  ) => {
    dispatch(toggleFiltering(true));
    dispatch(setColumnSetting({ columnSetting }));
    setTimeout(
      () => dispatch(toggleFiltering(false)),
      MSFilterDefaultValueConstants.StandardDelayTimeMs,
    );
  },
);

export const loadModelScreenerColumnSettingsThunk = createAsyncThunk(
  "modelScreeners/loadModelScreenerColumnSettings",
  async () => {
    return await getModelScreenerColumnSettings();
  },
);

export const createMSColumnSettingThunk = createAsyncThunk(
  "modelScreeners/createMSColumnSetting",
  async ({
    signal,
    columnSetting,
  }: {
    signal?: AbortSignal;
    columnSetting: ModelScreenerColumnSettingDTO;
  }) => {
    return await createMSColumnSetting(columnSetting, signal);
  },
);

export const updateMSColumnSettingThunk = createAsyncThunk(
  "modelScreeners/updateMSColumnSetting",
  async ({
    signal,
    columnSetting,
  }: {
    signal?: AbortSignal;
    columnSetting: ModelScreenerColumnSettingDTO;
  }) => {
    return await updateMSColumnSetting(columnSetting, signal);
  },
);

export const loadMSColumnSettingByIdThunk = createAsyncThunk(
  "modelScreeners/loadModelScreenerColumnSettingById",
  async (
    {
      selectedColumnSettingId,
      signal,
    }: {
      selectedColumnSettingId: string;
      signal?: AbortSignal;
    },
    { getState },
  ) => {
    const rootState = getState() as RootState;
    const modelScreenerState = rootState.modelScreener;
    const currentEditingColumnSetting =
      modelScreenerState.internal.currentEditingColumnSetting;
    const currentColumnSettings = modelScreenerState.columnSettings;

    if (
      !currentEditingColumnSetting ||
      !currentColumnSettings ||
      isEmpty(currentColumnSettings)
    )
      return;

    const selectedColumnSetting = currentColumnSettings.find(
      (setting) => setting.id === selectedColumnSettingId,
    );

    if (!selectedColumnSetting) return;

    return await getMSColumnSettingById({
      signal: signal,
      columnSettingId: selectedColumnSettingId,
    });
  },
);

export const deleteModelScreenerColumnSettingThunk = createAsyncThunk(
  "modelScreeners/deleteModelScreenerColumnSetting",
  async (columnSettingId: string) => {
    return await deleteModelScreenerFilterColumn(columnSettingId);
  },
);

export const setMSColumnSettingAsDefaultThunk = createAsyncThunk(
  "modelScreeners/setModelScreenerColumnSettingAsDefault",
  async (columnSettingId: string) => {
    return await setMSColumnSettingAsDefault(columnSettingId);
  },
);

export default modelScreenerSlice.reducer;
